import React, { useState, useMemo, useEffect } from 'react';
import { X, Check, Minus, Shield, Loader2, AlertCircle, Edit2, Save } from 'lucide-react';
import { countPermissions, type Role, type ObjectPermission, useUpdateRole } from '../../../services/queries/roleQueries';
import { useIsSystemAdmin } from '../../../services/queries/permissionQueries';
import { useToast } from '../../../contexts/ToastContext';
import { StandardButton } from '../../standard';

interface PermissionMatrixModalProps {
  role: Role | null;
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

export const PermissionMatrixModal: React.FC<PermissionMatrixModalProps> = ({
  role,
  isOpen,
  onClose,
  isLoading = false,
  error = null,
  onRetry
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedRole, setEditedRole] = useState<Role | null>(null);
  const { isSystemAdmin } = useIsSystemAdmin();
  const updateRoleMutation = useUpdateRole();
  const { success, error: showError } = useToast();

  // Initialize edited role when role changes
  useEffect(() => {
    if (role) {
      setEditedRole(role);
    }
  }, [role]);

  // Check if there are unsaved changes
  const isDirty = useMemo(() => {
    if (!role || !editedRole) return false;
    return JSON.stringify(role.permissions) !== JSON.stringify(editedRole.permissions);
  }, [role, editedRole]);

  // Handle permission toggle
  const handlePermissionToggle = (
    objectId: string,
    permissionType: 'read' | 'write' | 'delete'
  ) => {
    if (!isEditMode || !editedRole) return;

    setEditedRole((prev: Role | null) => {
      if (!prev) return prev;
      const updatedPermissions = prev.permissions.map((p: ObjectPermission) =>
        p.objectId === objectId
          ? { ...p, [permissionType]: !p[permissionType] }
          : p
      );
      return { ...prev, permissions: updatedPermissions };
    });
  };

  // Handle save
  const handleSave = async () => {
    if (!editedRole || !isDirty) return;

    try {
      await updateRoleMutation.mutateAsync({
        roleId: editedRole.id,
        updates: {
          permissions: editedRole.permissions,
        }
      });

      success('Permissions updated successfully');
      setIsEditMode(false);
    } catch (err) {
      showError('Failed to update permissions');
      console.error('Error updating permissions:', err);
    }
  };

  // Handle discard
  const handleDiscard = () => {
    if (isDirty) {
      if (window.confirm('Discard unsaved changes?')) {
        setEditedRole(role);
        setIsEditMode(false);
      }
    } else {
      setIsEditMode(false);
    }
  };

  // Handle close with unsaved changes
  const handleClose = () => {
    if (isDirty && isEditMode) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        setIsEditMode(false);
        setEditedRole(role);
        onClose();
      }
    } else {
      onClose();
    }
  };
  if (!isOpen) return null;

  // Loading state
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
          <p className="text-gray-600 dark:text-gray-300">Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-full">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Failed to load permissions
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {error.message || 'An error occurred while loading the permission matrix.'}
            </p>
            <div className="flex gap-3">
              {onRetry && (
                <StandardButton variant="primary" onClick={onRetry}>
                  Retry
                </StandardButton>
              )}
              <StandardButton variant="ghost" onClick={onClose}>
                Close
              </StandardButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No role selected
  if (!role) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <p className="text-gray-600 dark:text-gray-300">No role selected</p>
          <StandardButton variant="ghost" onClick={onClose} className="mt-4">
            Close
          </StandardButton>
        </div>
      </div>
    );
  }

  const permissionCounts = countPermissions(role);
  const displayRole = editedRole || role;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Permission Matrix</h2>
              {isEditMode && (
                <span className="px-2 py-1 text-xs font-semibold text-blue-700 bg-blue-100 dark:text-blue-300 dark:bg-blue-900/30 rounded">
                  EDITING
                </span>
              )}
              {isDirty && isEditMode && (
                <span className="px-2 py-1 text-xs font-semibold text-orange-700 bg-orange-100 dark:text-orange-300 dark:bg-orange-900/30 rounded">
                  UNSAVED CHANGES
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {isEditMode ? 'Editing' : 'Viewing'} permissions for: <span className="font-semibold">{role.name}</span>
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Role Info */}
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-purple-900 dark:text-purple-300 mb-1">
                    {role.name}
                  </h3>
                  {role.description && (
                    <p className="text-sm text-purple-800 dark:text-purple-300 mb-2">
                      {role.description}
                    </p>
                  )}
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="text-purple-700 dark:text-purple-300">
                        {Number(permissionCounts.read) || 0} Read
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <span className="text-purple-700 dark:text-purple-300">
                        {Number(permissionCounts.write) || 0} Write
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <span className="text-purple-700 dark:text-purple-300">
                        {Number(permissionCounts.delete) || 0} Delete
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Parent Role Info */}
            {role.parentRoleName && (
              <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <strong>Parent Role:</strong> {role.parentRoleName}
                  <span className="text-gray-500 dark:text-gray-400 ml-2">
                    (inherits permissions from parent)
                  </span>
                </p>
              </div>
            )}

            {/* Permissions Table */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Object Permissions
              </h3>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                        Object
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                        Read
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                        Write
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                        Delete
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                        Access Level
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {(!displayRole.permissions || displayRole.permissions.length === 0) && (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                          No object permissions configured for this role.
                        </td>
                      </tr>
                    )}
                    {displayRole.permissions?.map((permission: ObjectPermission) => {
                      let accessLevel = 'No Access';
                      let accessColor = 'text-gray-500 dark:text-gray-400';

                      if (permission.delete) {
                        accessLevel = 'Full Access';
                        accessColor = 'text-red-600 dark:text-red-400';
                      } else if (permission.write) {
                        accessLevel = 'Read/Write';
                        accessColor = 'text-blue-600 dark:text-blue-400';
                      } else if (permission.read) {
                        accessLevel = 'Read Only';
                        accessColor = 'text-green-600 dark:text-green-400';
                      }

                      const renderCheckbox = (permType: 'read' | 'write' | 'delete', isChecked: boolean) => {
                        const colors = {
                          read: { bg: 'bg-green-100 dark:bg-green-900/20', icon: 'text-green-600 dark:text-green-400', hover: 'hover:bg-green-200 dark:hover:bg-green-900/40' },
                          write: { bg: 'bg-blue-100 dark:bg-blue-900/20', icon: 'text-blue-600 dark:text-blue-400', hover: 'hover:bg-blue-200 dark:hover:bg-blue-900/40' },
                          delete: { bg: 'bg-red-100 dark:bg-red-900/20', icon: 'text-red-600 dark:text-red-400', hover: 'hover:bg-red-200 dark:hover:bg-red-900/40' },
                        };

                        const bgClass = isChecked ? colors[permType].bg : 'bg-gray-100 dark:bg-gray-700';
                        const iconClass = isChecked ? colors[permType].icon : 'text-gray-400';
                        const cursorClass = isEditMode ? 'cursor-pointer' : '';
                        const hoverClass = isEditMode && isChecked ? colors[permType].hover : isEditMode ? 'hover:bg-gray-200 dark:hover:bg-gray-600' : '';

                        return (
                          <div className="flex justify-center">
                            <button
                              type="button"
                              onClick={() => isEditMode && handlePermissionToggle(permission.objectId, permType)}
                              disabled={!isEditMode}
                              className={`w-6 h-6 rounded-full ${bgClass} ${cursorClass} ${hoverClass} flex items-center justify-center transition-colors`}
                            >
                              {isChecked ? (
                                <Check className={`w-4 h-4 ${iconClass}`} />
                              ) : (
                                <Minus className={`w-4 h-4 ${iconClass}`} />
                              )}
                            </button>
                          </div>
                        );
                      };

                      return (
                        <tr key={permission.objectId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                            {permission.objectName}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {renderCheckbox('read', permission.read)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {renderCheckbox('write', permission.write)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {renderCheckbox('delete', permission.delete)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-sm font-medium ${accessColor}`}>
                              {accessLevel}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Permission Legend */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Permission Levels Explained
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Minus className="w-3 h-3 text-gray-400" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">No Access</div>
                    <div className="text-gray-600 dark:text-gray-400">Cannot view or interact with records</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded bg-green-100 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">Read Only</div>
                    <div className="text-gray-600 dark:text-gray-400">Can view but not modify records</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">Read/Write</div>
                    <div className="text-gray-600 dark:text-gray-400">Can view, create, and edit records</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded bg-red-100 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">Full Access</div>
                    <div className="text-gray-600 dark:text-gray-400">Can view, create, edit, and delete records</div>
                  </div>
                </div>
              </div>
            </div>

            {/* System Role Warning */}
            {role.isSystem && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  <strong>System Role:</strong> This role is managed by the system and cannot be deleted.
                  Permissions can be modified by administrators.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center gap-2">
            {isEditMode && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Click permission checkboxes to toggle
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!isEditMode ? (
              <>
                {isSystemAdmin && !role.isSystem && (
                  <StandardButton
                    variant="primary"
                    onClick={() => setIsEditMode(true)}
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Permissions
                  </StandardButton>
                )}
                <StandardButton
                  variant="ghost"
                  onClick={handleClose}
                >
                  Close
                </StandardButton>
              </>
            ) : (
              <>
                <StandardButton
                  variant="ghost"
                  onClick={handleDiscard}
                  disabled={updateRoleMutation.isPending}
                >
                  {isDirty ? 'Discard Changes' : 'Cancel'}
                </StandardButton>
                <StandardButton
                  variant="primary"
                  onClick={handleSave}
                  disabled={!isDirty || updateRoleMutation.isPending}
                >
                  {updateRoleMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </StandardButton>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
